import React from "react";
import { FileText, Download, Sparkles, RefreshCw, Check, Circle } from "lucide-react";
import DemandPageHeader from "../components/DemandPageHeader";
import DemandSelector from "../components/DemandSelector";
import { useDemandId } from "../context/DemandContext";
import { get, post, demandPath, AI_TIMEOUT_MS } from "../api/http";

/**
 * Stage artifacts — the documents the pipeline leaves behind.
 *
 * Every stage already ran an AI agent and wrote its findings into columns, but
 * nothing readable came out of it: demand_stage_artifacts had zero rows. This is
 * where a triage report gets handed to a submitter, or a TAB pack to a board.
 */

const CARD = "rounded-2xl border border-gray-200 bg-white shadow-sm";

export default function DemandArtifactsPage() {
  const { demandId, setDemandId } = useDemandId();
  const [data, setData] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    if (!demandId) return;
    try {
      setError("");
      const d = await get(demandPath(demandId, "/artifacts"));
      setData(d);
      // Keep the open document in view across a regenerate.
      setSelected((prev) => {
        const match = prev && d.artifacts.find((a) => a.stage_name === prev.stage_name);
        return match || d.artifacts[0] || null;
      });
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [demandId]);

  React.useEffect(() => { setSelected(null); load(); }, [load]);

  const [body, setBody] = React.useState("");
  React.useEffect(() => {
    if (!selected || !demandId) { setBody(""); return; }
    get(demandPath(demandId, `/artifacts/${selected.id}`))
      .then((a) => setBody(a.content || "_This artifact has no inline content._"))
      .catch((e) => setBody(`Could not load: ${e.message}`));
  }, [selected, demandId]);

  const generate = async (stage, narrate) => {
    setBusy(stage);
    setError("");
    try {
      await post(demandPath(demandId, `/artifacts/${stage}/generate`), { narrate },
                 { timeout: AI_TIMEOUT_MS });
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const download = () => {
    // Blob rather than a data: URI so large documents don't hit URL length limits.
    const blob = new Blob([body], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = selected?.file_name || `${demandId}-artifact.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const header = (
    <DemandPageHeader
      title="Stage Artifacts"
      description="Generated reports for each stage — the record a submitter, ISSO or board can be handed"
      breadcrumbPath="Stage Artifacts"
    >
      <DemandSelector currentDemandId={demandId} stage="" onDemandChange={setDemandId} />
    </DemandPageHeader>
  );

  if (!demandId) {
    return (
      <div>{header}
        <div style={{ padding: 40, textAlign: "center", color: "var(--cares-text-label)" }}>
          Select a demand to see its artifacts.
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div className="mx-auto max-w-7xl px-2 pb-10">
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            {error}
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-3">
          <section className={`${CARD} p-4 lg:col-span-1`}>
            <h3 className="mb-3 text-base text-gray-900">Documents</h3>
            <div className="space-y-2">
              {(data?.availableToGenerate || []).map((s) => {
                const existing = (data?.artifacts || []).find(
                  (a) => a.stage_name === s.stage && a.artifact_type === "report");
                const isOpen = selected?.id === existing?.id;
                return (
                  <div key={s.stage}
                       className={`rounded-xl border p-3 transition ${
                         isOpen ? "border-indigo-300 bg-indigo-50/50" : "border-gray-200"}`}>
                    <div className="flex items-center gap-2">
                      {s.generated
                        ? <Check size={14} className="shrink-0 text-emerald-600" />
                        : <Circle size={14} className="shrink-0 text-gray-300" />}
                      <button
                        disabled={!existing}
                        onClick={() => setSelected(existing)}
                        className={`min-w-0 flex-1 truncate text-left text-sm font-medium ${
                          existing ? "text-gray-900 hover:text-indigo-700" : "text-gray-400"}`}
                      >
                        {s.title}
                      </button>
                      {existing && (
                        <span className="shrink-0 text-[11px] text-gray-500">v{existing.version}</span>
                      )}
                    </div>
                    <div className="mt-2 flex gap-1.5">
                      <button onClick={() => generate(s.stage, false)} disabled={busy !== null}
                              className="rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {busy === s.stage ? "Working…" : s.generated ? "Regenerate" : "Generate"}
                      </button>
                      {/* Narration is opt-in per document: it costs an AI call and
                          the factual body is identical either way. */}
                      <button onClick={() => generate(s.stage, true)} disabled={busy !== null}
                              title="Generate with an AI executive preface"
                              className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
                        <Sparkles size={11} /> + AI
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={load}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
              <RefreshCw size={12} /> Refresh
            </button>
          </section>

          <section className={`${CARD} lg:col-span-2`}>
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <FileText size={16} className="shrink-0 text-indigo-600" />
                <h3 className="truncate text-base text-gray-900">
                  {selected?.title || "No document selected"}
                </h3>
                {selected?.generated_by === "ai" && (
                  <span className="shrink-0 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                    AI preface
                  </span>
                )}
              </div>
              {selected && (
                <button onClick={download}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  <Download size={13} /> Download .md
                </button>
              )}
            </div>
            <div className="p-5">
              {selected ? (
                // Rendered as preformatted text rather than parsed markdown: this is
                // a governance record, and showing exactly what is stored is the
                // point. The download is the same bytes.
                <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-4 text-xs leading-relaxed text-gray-800">
                  {body || "Loading…"}
                </pre>
              ) : (
                <p className="py-16 text-center text-sm text-gray-500">
                  Generate a document from the list to view it here.
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
